import { inject, injectable } from 'inversify';

import type {
  AddAdyenPaymentDetails,
  CreateOrder,
  DeletePaymentMethod,
  FinalizeAdyenPaymentDetails,
  ForwardStoreReceipt,
  GetAdyenPaymentSession,
  GetDirectPostCardPayment,
  GetEntitlements,
  GetFinalizeAdyenPayment,
  GetInitialAdyenPayment,
  GetMediaOffers,
  GetOffer,
  GetOffers,
  GetOrder,
  GetPaymentMethods,
  GetSubscriptionOffers,
  GetSubscriptionSwitch,
  GetSubscriptionSwitches,
  Offer,
  Order,
  Payment,
  PaymentWithoutDetails,
  PaymentWithPayPal,
  SwitchSubscription,
  UpdateOrder,
  UpdatePaymentWithPayPal,
} from '../../../../types/checkout';
import CheckoutService from '../CheckoutService';

import StreamIQData from './StreamIQData';

@injectable()
export default class StreamIQCheckoutService extends CheckoutService {
  constructor(@inject(StreamIQData) private readonly data: StreamIQData) {
    super();
  }

  getOffer?: GetOffer = async () => {
    throw new Error('Offer not found');
  };

  getOffers: GetOffers = async () => {
    return [];
  };
  getMediaOffers: GetMediaOffers = async ({ mediaProducts }) => {
    const products = this.data.getProducts();

    return mediaProducts.reduce((previousValue, { id }, productIndex) => {
      const product = products.find((item) => item.id === id);

      if (!product) return previousValue;

      const priceDecimal = product.priceIncTax / 100;

      return [
        ...previousValue,
        {
          offerId: id,
          id: productIndex,
          type: 'pass',
          offerPrice: priceDecimal,
          discountedCustomerPriceInclTax: priceDecimal,
          discountedCustomerPriceExclTax: priceDecimal,
          offerCurrency: product.currency,
          customerPriceInclTax: priceDecimal,
          customerPriceExclTax: priceDecimal,
          customerCurrency: product.currency,
          offerTitle: product.name,
          active: true,
          period: 'day',
          durationPeriod: 'hours',
          durationAmount: product.rentalPeriodInHours,
        } satisfies Offer,
      ];
    }, [] as Offer[]);
  };

  getSubscriptionOffers?: GetSubscriptionOffers | undefined;
  createOrder: CreateOrder = async ({ offer, customerId, country }) => {
    return {
      errors: [],
      responseData: {
        order: {
          id: 0,
          customerId,
          customer: {
            locale: '',
            email: '',
          },
          publisherId: 0,
          offerId: offer.offerId,
          offer: {
            title: offer.offerTitle,
            description: offer.offerDescription || '',
            price: offer.customerPriceInclTax,
            currency: offer.customerCurrency,
          },
          totalPrice: offer.customerPriceInclTax,
          priceBreakdown: {
            offerPrice: offer.offerPrice,
            discountAmount: 0,
            discountedPrice: 0,
            taxValue: 0,
            customerServiceFee: 0,
            paymentMethodFee: 0,
          },
          taxRate: 0,
          taxBreakdown: null,
          currency: 'EUR',
          country,
          paymentMethodId: 0,
          expirationDate: 0,
          billingAddress: null,
          couponId: null,
          discount: {
            applied: false,
            type: '',
            periods: 0,
          },
          requiredPaymentDetails: false,
        } satisfies Order,
        message: '',
        status: 200,
        success: true,
      },
    };
  };
  updateOrder: UpdateOrder = async () => {
    throw new Error('Not implemented');
  };
  getPaymentMethods: GetPaymentMethods = async () => {
    return {
      errors: [],
      responseData: {
        message: '',
        paymentMethods: [],
        status: 200,
      },
    };
  };
  getEntitlements: GetEntitlements = async ({ offerId }) => {
    const purchase = this.data.getPurchase(offerId);

    return {
      errors: [],
      responseData: {
        accessGranted: !!purchase,
        expiresAt: purchase?.exp || -1,
      },
    };
  };
  directPostCardPayment: GetDirectPostCardPayment = async () => {
    throw new Error('Not implemented');
  };
  paymentWithoutDetails: PaymentWithoutDetails = async ({ order, offer }) => {
    await new Promise((resolve) => setTimeout(resolve, 2000));

    this.data.registerPurchase(offer.offerId, offer.durationAmount || 48);

    return {
      errors: [],
      responseData: {
        id: order.id,
        orderId: order.id,
        status: '',
        totalAmount: order.totalPrice,
        currency: order.currency,
        customerId: order.customerId,
        paymentGateway: '',
        paymentMethod: '',
        externalPaymentId: '',
        couponId: null,
        amount: 0,
        country: order.country || '',
        offerType: offer.type,
        taxValue: 0,
        paymentMethodFee: 0,
        customerServiceFee: 0,
        rejectedReason: null,
        refundedReason: null,
        paymentDetailsId: null,
        paymentOperation: '',
      } satisfies Payment,
    };
  };
  paymentWithPayPal: PaymentWithPayPal = async () => {
    throw new Error('Not implemented');
  };

  // optional
  getOrder?: GetOrder | undefined;
  switchSubscription?: SwitchSubscription | undefined;
  getSubscriptionSwitches?: GetSubscriptionSwitches | undefined;
  getSubscriptionSwitch?: GetSubscriptionSwitch | undefined;
  createAdyenPaymentSession?: GetAdyenPaymentSession | undefined;
  initialAdyenPayment?: GetInitialAdyenPayment | undefined;
  finalizeAdyenPayment?: GetFinalizeAdyenPayment | undefined;
  updatePaymentMethodWithPayPal?: UpdatePaymentWithPayPal | undefined;
  deletePaymentMethod?: DeletePaymentMethod | undefined;
  addAdyenPaymentDetails?: AddAdyenPaymentDetails | undefined;
  finalizeAdyenPaymentDetails?: FinalizeAdyenPaymentDetails | undefined;
  forwardStoreReceipt?: ForwardStoreReceipt | undefined;
}
